import { validateFileType } from '../../utils/documentValidation';
import { DOCUMENT_CONSTANTS } from '../../utils/constants/MilestoneConstants';
import { DEFAULT_TIMEZONE } from '../../utils/timezone';
import moment from 'moment-timezone';

export const collectValidFilesToUpload = (validation, newErrors, multiple) => {
    const validFilesToUpload = [];
    for (const file of validation.validFiles) {
        const fileTypeValidation = validateFileType(file);
        if (!fileTypeValidation.isValid) {
            newErrors.push({ name: file.name, error: fileTypeValidation.error });
            continue;
        }

        if (file.size > DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES) {
            newErrors.push({
                name: file.name,
                error: `File "${file.name}" exceeds ${DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB} MB.`,
            });
            continue;
        }

        validFilesToUpload.push(file);
        if (!multiple) {
            break;
        }
    }
    return validFilesToUpload;
};

export const buildOptimisticFiles = (validFilesToUpload) =>
    validFilesToUpload.map((file) => {
        const enrichedFile = file;
        enrichedFile.isUploading = true;
        enrichedFile.created_at = moment.tz(DEFAULT_TIMEZONE).toISOString();
        enrichedFile.file_name = enrichedFile.name;
        enrichedFile.file = enrichedFile;
        return enrichedFile;
    });

export const attachUploadMetadata = (targetFile, payload) => {
    const actualPayload = payload?.payload || payload;
    const serverTimestamp = actualPayload?.created_at || payload?.created_at;
    const createdAt = serverTimestamp
        ? moment.utc(serverTimestamp).tz(DEFAULT_TIMEZONE).toISOString()
        : moment.tz(DEFAULT_TIMEZONE).toISOString();
    targetFile.document_ulid = actualPayload?.document_ulid || payload?.document_ulid;
    targetFile.file_name = actualPayload?.file_name || payload?.file_name || targetFile.name;
    targetFile.created_at = createdAt;
    targetFile.isUploading = false;
    targetFile.file = targetFile;
    return targetFile;
};

export const getSelectedFileKey = (file, index) =>
    file?.document_ulid || file?.ulid || file?.file_name || file?.name || file?.file?.name || `uploaded-file-${index}`;

export const getErrorFileKey = (file, index) => `${file?.name || 'error'}-${file?.error || index}`;

export const shouldShowDeleteFileAction = ({ isEditable, isDeletedFile, multiple, file }) =>
    (isEditable && !isDeletedFile && !multiple) ||
    (multiple && (file.name || file.file_name || file.file?.name) && !isDeletedFile && file.isUploading !== true);

export const shouldShowFileNameChip = ({ file, isDeletedFile, multiple }) =>
    (file.name || file.file?.name) && (!isDeletedFile || multiple);

export const getUploadDisabledStyle = (uploadDisabled) => ({
    opacity: uploadDisabled ? 0.6 : 1,
    cursor: uploadDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: uploadDisabled ? 'none' : 'auto',
});

export const createFileLikeFromDocument = (entry) => {
    const fileLike = new File([], entry.file_name || entry.name || 'Document', {
        type: entry.type || 'application/pdf',
    });
    fileLike.document_ulid = entry.document_ulid || entry.ulid;
    fileLike.file_name = entry.file_name || entry.name;
    fileLike.ulid = entry.ulid || entry.document_ulid;
    fileLike.file = fileLike;
    fileLike.isUploading = entry.isUploading || false;
    fileLike.created_at = entry.created_at;
    return fileLike;
};

export const applyOptimisticSelection = ({
    validFilesToUpload,
    newErrors,
    errors,
    multiple,
    selectedFiles,
    setSelectedFiles,
    setSelectedFilesProp,
    setErrors,
}) => {
    if (validFilesToUpload.length > 0) {
        const optimisticFiles = buildOptimisticFiles(validFilesToUpload);
        const updatedFiles = multiple ? [...selectedFiles, ...optimisticFiles] : [...optimisticFiles];
        setSelectedFiles(updatedFiles);
        setSelectedFilesProp?.(updatedFiles);
        if (newErrors.length > 0) {
            setErrors([...errors, ...newErrors]);
        }
        return;
    }
    if (newErrors.length > 0) {
        setErrors([...errors, ...newErrors]);
    }
};

export const createFileUploadTask = ({
    file,
    handleImageUpload,
    uploadedPayloads,
    setSelectedFiles,
    setSelectedFilesProp,
    setErrors,
    matchFile,
}) =>
    (async () => {
        try {
            const payload = await handleImageUpload(file);
            if (!payload) {
                throw new Error('No response from server');
            }
            uploadedPayloads.push(payload);
            setSelectedFiles((prevFiles) => {
                const updated = [...prevFiles];
                const fileIndex = updated.findIndex((f) => matchFile(f, file));
                if (fileIndex !== -1) {
                    const targetFile = updated[fileIndex] instanceof File ? updated[fileIndex] : updated[fileIndex]?.file;
                    if (targetFile) {
                        updated[fileIndex] = attachUploadMetadata(targetFile, payload);
                    }
                }
                setSelectedFilesProp?.(updated);
                return updated;
            });
            return { file: file, success: true };
        } catch (error) {
            setSelectedFiles((prevFiles) => {
                const filtered = prevFiles.filter((f) => !matchFile(f, file));
                setSelectedFilesProp?.(filtered);
                return filtered;
            });
            const errorMessage = error?.message || `File "${file.name}" upload failed.`;
            const fileError = { name: file.name, error: errorMessage };
            setErrors((prev) => [...prev, fileError]);
            return { file: null, success: false, error: fileError };
        }
    })();

